"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { rosterApi, type RosterMemberRole } from "@/shared/api/roster";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { Button } from "@/shared/ui/buttons/button";
import { Label } from "@/shared/ui/forms/label";
import { RolePicker } from "@/shared/ui/inputs/role-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";

interface ApplyToTeamModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  /** Свободные роли для подачи заявки (незанятые линии + «Запас»). */
  freeRoles: RosterMemberRole[];
  onApplied?: () => void;
}

export function ApplyToTeamModal({
  open,
  onClose,
  teamId,
  teamName,
  freeRoles,
  onApplied,
}: ApplyToTeamModalProps) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const [role, setRole] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setRole(freeRoles[0] ?? "");
  }, [open, freeRoles]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!role || submitting) return;
      setSubmitting(true);
      try {
        const res = await rosterApi.submitApplication(
          teamId,
          role as RosterMemberRole,
        );
        if (res?.success === false) {
          toast.error(tToast("applyToTeamModal.neUdalosPodatZayavku"));
          return;
        }
        toast.success(tToast("applyToTeamModal.zayavkaOtpravlenaKapitanuKomandy"));
        onApplied?.();
        onClose();
      } catch (error) {
        toast.error(extractApiErrorMessage(error, tUi("litApplyToTeamModal.neUdalosPodatZayavku")));
      } finally {
        setSubmitting(false);
      }
    },
    [role, submitting, teamId, onApplied, onClose, tToast, tUi],
  );


  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{tUi("applyToTeamModal.podatZayavku")}</DialogTitle>
          <DialogDescription>
            Заявка в команду «{teamName}». Выберите роль, на которую хотите попасть —
            капитан рассмотрит её.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="apply-role">{tUi("applyToTeamModal.rol")}</Label>
              {/* KAN-368: выбор роли — плиткой, как при смене роли игрока в составе.
                  Здесь стоял Select со вложенным RoleCell (ячейкой таблицы состава):
                  бокс 40×40 не помещался в строку триггера, триггер был уже списка,
                  а сам список перекрывал кнопки формы. */}
              {freeRoles.length > 0 ? (
                <RolePicker
                  idPrefix="apply-role"
                  roles={freeRoles}
                  value={role}
                  onChange={setRole}
                  disabled={submitting}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {tUi("applyToTeamModal.svobodnyhRoleyVEtoy")}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {tUi("applyToTeamModal.otmena")}
            </Button>
            <Button type="submit" disabled={!role || submitting || freeRoles.length === 0}>
              {submitting ? tUi("litApplyToTeamModal.otpravka") : tUi("litApplyToTeamModal.podatZayavku")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
